import React, { useState } from "react"
import {
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	Grid,
	IconButton
} from "@material-ui/core"
import {
	Person as ActiveFlowIcon,
	Delete as DeleteIcon
} from "@material-ui/icons"

import { ActionDialog, Divider, PopConfirm, ProductReportClient } from "@/components"
import ChatBotGroupPermissionGate from "@/components/PermissionGate/ChatBotGroupPermissionGate"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/ChatBot/ChatBotConstructor/components/ActiveFlowReport/styles"

import { ActiveInboxChannelChatFlow, FlowData } from "@/protocols/chatBotConstructor"
import ChatBotFlowService, { RemoveContactsFromFlowAction } from "@/services/ChatBotFlow"
import ErrorHandlerService from "@/services/ErrorHandler"

type ActiveFlowReportProps = {
	activeInboxChannelChatFlows: ActiveInboxChannelChatFlow[]
	tooltipTitle?: string
	dialogTitle?: string
	buttonClassName?: string
	chatBotFlowId?: number
	changeChatBotFlow: (flow: Partial<Pick<FlowData["flow"], "active" | "name" | "updated_at" | "active_inbox_channel_chat_flows">>) => void
	nextChatBotFlowBlockId?: number
	buttonTitle?: string
}

const ActiveFlowReport: React.FC<ActiveFlowReportProps> = (props) => {
	const {
		tooltipTitle,
		buttonClassName,
		dialogTitle,
		activeInboxChannelChatFlows,
		chatBotFlowId,
		changeChatBotFlow,
		nextChatBotFlowBlockId,
		buttonTitle
	} = props

	const [opened, setOpened] = useState(false)
	const filteredActiveInboxChannelChatFlows =
		nextChatBotFlowBlockId ? activeInboxChannelChatFlows?.filter(flow => flow.current_chat_bot_flow_block_id === nextChatBotFlowBlockId) : activeInboxChannelChatFlows

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const handleOpenDialog = () => {
		setOpened(true)
	}

	const handleCloseDialog = () => {
		setOpened(false)
	}

	const handleFilterActiveInboxChannelChatFlows = (inboxChannelChatFlowIds: number[]) => {
		const updatedActiveInboxChannelChatFlows = activeInboxChannelChatFlows.filter((activeInboxChannelChatFlow) => {
			return !inboxChannelChatFlowIds.includes(activeInboxChannelChatFlow.id)
		})

		changeChatBotFlow({
			active_inbox_channel_chat_flows: [
				...updatedActiveInboxChannelChatFlows
			]
		})
	}

	const handleRemoveContact = async (action: RemoveContactsFromFlowAction, inboxChannelChatFlowIds?: number[]) => {
		PopConfirm.open({
			title: "EXCLUIR CLIENTE DO FLUXO/BLOCO",
			description: "Tem certeza? Essa ação é irreversível.",
			onConfirm: async () => {
				try {
					await ChatBotFlowService.removeContactsFromFlow(Number(chatBotFlowId), action, inboxChannelChatFlowIds)
					handleFilterActiveInboxChannelChatFlows(inboxChannelChatFlowIds || [])
				} catch (error) {
					ErrorHandlerService.handle(error)
				}
			},
			confirmButtonText: "EXCLUIR"
		})
	}

	return (
		<>
			<Tooltip
				title={tooltipTitle || ""}
			>
				<Button
					variant="contained"
					color="primary"
					startIcon={<ActiveFlowIcon />}
					onClick={handleOpenDialog}
					className={`${classes.button} ${buttonClassName}`}
				>
					{filteredActiveInboxChannelChatFlows.length}
				</Button>
			</Tooltip>

			<ActionDialog
				title={dialogTitle || "CONTATOS"}
				hideCloseButton
				onClose={handleCloseDialog}
				openDialog={opened}
				maxWidth="sm"
				fullWidth
			>
				{filteredActiveInboxChannelChatFlows?.length > 0 ? (
					<>
						<Grid container justifyContent="flex-end">
							<ChatBotGroupPermissionGate action="update">
								<Button
									variant="contained"
									onClick={() => handleRemoveContact("removeAllContactsOfChatBotFlow")}
									className={classes.saveButton}
									endIcon={<DeleteIcon />}
									color="primary"
								>
									{ buttonTitle }
								</Button>
							</ChatBotGroupPermissionGate>
						</Grid>

						<Divider orientation="horizontal" size={1} />

						<TableContainer>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											ID Bloco Atual
										</TableCell>

										<TableCell>
											Contato
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody
									className={customClasses.reportTableBodyText}
								>
									{filteredActiveInboxChannelChatFlows?.map(activeFlow => (
										<TableRow
											key={activeFlow.id}
											tabIndex={-1}
										>
											<TableCell>
												{activeFlow.current_chat_bot_flow_block_id}
											</TableCell>

											<TableCell>
												<Grid container alignItems="center" justifyContent="center">
													<ProductReportClient
														client={activeFlow.client}
													/>

													<ChatBotGroupPermissionGate action="update">
														<IconButton
															onClick={() => handleRemoveContact("removeOneContactsOfChatBotFlow", [activeFlow.id])}
														>
															<DeleteIcon />
														</IconButton>
													</ChatBotGroupPermissionGate>
												</Grid>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				) : (
					<Typography
						variant="body1"
					>
						Nenhum contato está aqui atualmente
					</Typography>
				)}
			</ActionDialog>
		</>
	)
}

export default ActiveFlowReport
